import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { categories, feedItems, siteInfo, timeSlots } from '@/mocks/feed';
import { useSeo } from '@/hooks/useSeo';
import FeedCard from '@/components/feature/FeedCard';
import FeedSidebar from './components/FeedSidebar';
import Breadcrumb from '@/components/feature/Breadcrumb';

const ALL_CAT = '全部栏目';
const ALL_SLOT = '全部时段';
const PAGE_SIZE = 6;

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '瓜田情报库',
    url: `${siteInfo.domain}/feed`,
    inLanguage: 'zh-CN',
    description: '按栏目与时段汇总深夜话题条目，支持关键词搜索与分类筛选。',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: `${siteInfo.domain}/` },
      { '@type': 'ListItem', position: 2, name: '瓜田情报库', item: `${siteInfo.domain}/feed` },
    ],
  },
];

export default function Feed() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const cat = params.get('cat') ?? ALL_CAT;
  const slot = params.get('slot') ?? ALL_SLOT;

  const [keyword, setKeyword] = useState(q);
  const [visible, setVisible] = useState(PAGE_SIZE);

  useSeo({
    title: '瓜田情报库｜深夜话题与午夜黑料条目汇总',
    description:
      '瓜田情报库按栏目与时段汇总所有深夜话题条目，支持关键词搜索、塌房现场、反转连续剧等分类筛选，午夜到后半夜一次看全。',
    keywords: '深夜黑料条目,午夜话题汇总,后半夜吃瓜,塌房现场,反转连续剧',
    path: '/feed',
    ogType: 'website',
    jsonLd,
  });

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [q, cat, slot]);

  const updateParam = (key: string, value: string, fallback: string) => {
    const next = new URLSearchParams(params);
    if (!value || value === fallback) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    return feedItems.filter((item) => {
      const matchCat = cat === ALL_CAT || item.category === cat;
      const matchSlot = slot === ALL_SLOT || item.timeSlot === slot;
      const haystack = `${item.title} ${item.summary} ${item.category} ${item.timeSlot} ${item.author}`.toLowerCase();
      const matchKw = !kw || haystack.includes(kw);
      return matchCat && matchSlot && matchKw;
    });
  }, [q, cat, slot]);

  const shown = filtered.slice(0, visible);
  const hasFilter = Boolean(q) || cat !== ALL_CAT || slot !== ALL_SLOT;

  const resetAll = () => {
    setParams(new URLSearchParams(), { replace: true });
    setKeyword('');
  };

  return (
    <main className="mx-auto w-full max-w-[1440px] px-4 pt-24 pb-16 md:px-8 md:pt-28 md:pb-20">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '瓜田情报库' }]} />

      <header className="mt-6 max-w-3xl">
        <h1 className="font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">瓜田情报库</h1>
        <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
          这里收着所有已整理的深夜黑料条目，按出现时间倒序排列。
          你可以用关键词找人找事，也可以直接按栏目或时段切进去，看看某个时间段里大家都在讨论什么。
        </p>
      </header>

      <section className="mt-8 rounded-lg border border-background-200/70 bg-background-100/50 p-4 md:p-5">
        <form
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            updateParam('q', keyword, '');
          }}
          className="flex flex-col gap-3 sm:flex-row"
        >
          <div className="relative flex-1">
            <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-500" />
            <input
              type="search"
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                updateParam('q', e.target.value, '');
              }}
              placeholder="搜索话题关键词，例如：反转、凌晨、旧瓜"
              aria-label="搜索话题条目"
              className="h-11 w-full rounded-md border border-background-200 bg-background-50 pl-10 pr-4 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </div>
          {hasFilter ? (
            <button
              type="button"
              onClick={resetAll}
              className="h-11 whitespace-nowrap rounded-md border border-background-300 px-4 font-label text-sm text-foreground-700 transition-colors hover:border-primary-500/50 hover:text-primary-300"
            >
              清空筛选
            </button>
          ) : null}
        </form>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="font-label text-xs text-foreground-500">栏目</span>
          {[ALL_CAT, ...categories].map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => updateParam('cat', item, ALL_CAT)}
              className={`cursor-pointer rounded-full border px-3 py-1.5 text-xs transition-colors ${
                cat === item
                  ? 'border-primary-500/60 bg-primary-500/15 text-primary-200'
                  : 'border-background-200 text-foreground-600 hover:border-background-300 hover:text-foreground-900'
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="font-label text-xs text-foreground-500">时段</span>
          {[ALL_SLOT, ...timeSlots.map((s) => s.key)].map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => updateParam('slot', item, ALL_SLOT)}
              className={`cursor-pointer rounded-full border px-3 py-1.5 text-xs transition-colors ${
                slot === item
                  ? 'border-accent-500/60 bg-accent-500/15 text-accent-200'
                  : 'border-background-200 text-foreground-600 hover:border-background-300 hover:text-foreground-900'
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </section>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-8">
          <p className="mb-5 text-xs text-foreground-500">
            共找到 <span className="text-foreground-900">{filtered.length}</span> 条条目
            {cat !== ALL_CAT ? ` · 栏目：${cat}` : ''}
            {slot !== ALL_SLOT ? ` · 时段：${slot}` : ''}
            {q ? ` · 关键词：${q}` : ''}
          </p>

          {shown.length > 0 ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {shown.map((item) => (
                <FeedCard key={item.slug} item={item} showHeat />
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100/40 px-6 py-16 text-center">
              <i className="ri-ghost-2-line text-4xl text-foreground-500" />
              <h2 className="mt-4 font-heading text-base font-bold text-foreground-900">这个时段还没有匹配的条目</h2>
              <p className="mt-2 text-sm text-foreground-600">换一个关键词，或者先把筛选条件清空试试。</p>
              <button
                type="button"
                onClick={resetAll}
                className="mt-5 inline-flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 font-label text-sm text-white transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line" />
                查看全部条目
              </button>
            </div>
          )}

          {visible < filtered.length ? (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="inline-flex h-11 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 font-label text-sm text-foreground-800 transition-colors hover:border-primary-500/50 hover:text-primary-300"
              >
                <i className="ri-add-line" />
                加载更多条目
              </button>
            </div>
          ) : null}
        </div>

        <div className="lg:col-span-4">
          <FeedSidebar />
        </div>
      </div>
    </main>
  );
}